// FAQ Widget Utilities
// Core logic for fetching and parsing FAQ data from Google Sheets

const SHEET_ID = '1qGg4bNlPUDXhQg4co7MVdGJhSMgrc7AP5DGO6Y__DIs'
const SHEET_NAME = 'FlowfeedFAQs'

export type FAQItemData = {
  answer: string
  question: string
}

/**
 * Fetches FAQ data from published Google Sheets CSV
 */
export async function loadFAQs(): Promise<FAQItemData[]> {
  const url = `https://docs.google.com/spreadsheets/d/${SHEET_ID}/gviz/tq?tqx=out:csv&sheet=${encodeURIComponent(SHEET_NAME)}`
  const response = await fetch(url)

  if (!response.ok) {
    throw new Error('Failed to load FAQs')
  }

  const csvText = await response.text()
  return parseCSV(csvText)
}

/**
 * Parse CSV with multi-line cell support
 * Standard CSV parsing breaks because answers contain line breaks for bullet points
 * Google Sheets wraps multi-line cells in quotes
 */
function parseCSV(csvText: string): FAQItemData[] {
  const lines = csvText.split('\n')
  const faqs: FAQItemData[] = []
  let i = 1 // Skip header row

  while (i < lines.length) {
    const result = parseCSVRow(lines, i)
    if (result.question && result.answer) {
      faqs.push({
        answer: result.answer,
        question: result.question,
      })
    }
    i = result.nextIndex
  }

  return faqs
}

type ParseResult = {
  answer: string
  nextIndex: number
  question: string
}

/**
 * Parse single row, handling cells that span multiple lines
 */
function parseCSVRow(lines: string[], startIndex: number): ParseResult {
  let question = ''
  let answer = ''
  let currentIndex = startIndex
  let line = lines[currentIndex]

  if (!line || !line.trim()) {
    return { answer: '', nextIndex: currentIndex + 1, question: '' }
  }

  // Parse question (Column A)
  let pos = 0
  if (line[pos] === '"') {
    pos++ // Skip opening quote
    let questionEnd = pos

    while (questionEnd < line.length) {
      if (line[questionEnd] === '"') {
        if (line[questionEnd + 1] === '"') {
          // Escaped quote ("")
          questionEnd += 2
        } else {
          // End of question
          question = line.substring(pos, questionEnd).replace(/""/g, '"').trim()
          pos = questionEnd + 1
          break
        }
      } else if (questionEnd === line.length - 1) {
        // Multi-line question - continue to next line
        currentIndex++
        if (currentIndex < lines.length) {
          line += '\n' + lines[currentIndex]
        }
      }
      questionEnd++
    }
  }

  // Skip comma separator
  if (line[pos] === ',') pos++

  // Parse answer (Column B)
  if (pos < line.length && line[pos] === '"') {
    pos++ // Skip opening quote
    const answerStart = pos
    let inQuotes = true

    while (inQuotes) {
      if (pos >= line.length) {
        // Multi-line answer - continue to next line
        currentIndex++
        if (currentIndex < lines.length) {
          line += '\n' + lines[currentIndex]
        } else {
          break
        }
      } else if (line[pos] === '"') {
        if (pos + 1 < line.length && line[pos + 1] === '"') {
          // Escaped quote
          pos += 2
        } else {
          // End of answer
          answer = line.substring(answerStart, pos).replace(/""/g, '"').trim()
          inQuotes = false
        }
      } else {
        pos++
      }
    }
  }

  return {
    answer,
    nextIndex: currentIndex + 1,
    question,
  }
}

/**
 * Escape HTML to prevent XSS attacks
 */
function escapeHtml(text: string): string {
  const div = document.createElement('div')
  div.textContent = text
  return div.innerHTML
}

/**
 * Format answer text with safe HTML tags
 * 1. Escape ALL HTML first (prevent XSS)
 * 2. Convert line breaks to <br>
 * 3. Selectively un-escape safe tags (bold, italic, links)
 */
export function formatAnswer(text: string): string {
  // Step 1: Escape everything
  const escaped = escapeHtml(text)

  // Step 2: Convert line breaks
  let formatted = escaped.replace(/\n/g, '<br>')

  // Step 3: Selectively allow safe HTML tags

  // Bold
  formatted = formatted.replace(/&lt;b&gt;/g, '<b>')
  formatted = formatted.replace(/&lt;\/b&gt;/g, '</b>')
  formatted = formatted.replace(/&lt;strong&gt;/g, '<strong>')
  formatted = formatted.replace(/&lt;\/strong&gt;/g, '</strong>')

  // Italic
  formatted = formatted.replace(/&lt;i&gt;/g, '<i>')
  formatted = formatted.replace(/&lt;\/i&gt;/g, '</i>')
  formatted = formatted.replace(/&lt;em&gt;/g, '<em>')
  formatted = formatted.replace(/&lt;\/em&gt;/g, '</em>')

  // Links (handle multiple quote formats from Google Sheets)
  formatted = formatted.replace(
    /&lt;a href=&quot;([^&]+)&quot;&gt;/g,
    '<a href="$1" target="_blank" rel="noopener noreferrer">',
  )
  formatted = formatted.replace(
    /&lt;a href=&#39;([^&]+)&#39;&gt;/g,
    '<a href="$1" target="_blank" rel="noopener noreferrer">',
  )
  formatted = formatted.replace(/&lt;a href="([^"]+)"&gt;/g, '<a href="$1" target="_blank" rel="noopener noreferrer">')
  formatted = formatted.replace(/&lt;a href='([^']+)'&gt;/g, '<a href="$1" target="_blank" rel="noopener noreferrer">')
  formatted = formatted.replace(/&lt;\/a&gt;/g, '</a>')

  return formatted
}
